import { createTheme } from "@mui/material";

// Modernized theme — visual polish on the legacy palette.
//
// Brand identity preserved: #0078FF blue primary, #F29C27 orange
// secondary, all the hardcoded `taskItem` status colors.
//
// Dark mode was scoped out — too many feature stylesheets bake in
// hardcoded #fff backgrounds, gray text, and non-theme-aware hover
// states (DayPilot timeplan calendar, ranks SortableList,
// crew-changes "planned" row striping, users SeafarersView, etc).
// Shipping a half-tinted dark mode looked worse than not having one.
// Tracked as an open item in docs/migration/tech-debt.md ("MUI v9 native
// dark mode") — needs a per-feature styles audit before it's
// flippable for end users.
//
// Kept from the dark-mode prototype:
//   - Modern typography (Inter + system fallback, refined scale,
//     non-CAPS buttons)
//   - Refined shape (8px borderRadius default, 12px dialogs)
//   - Layered soft shadow scale
//   - Component overrides that only sharpen the visual layer
//     without changing component contracts (no native-button vs
//     non-native shifts, no MenuItem geometry breaks)

const fontStack = [
  'Inter',
  // Apple system stack
  '-apple-system',
  'BlinkMacSystemFont',
  'San Francisco',
  // Other modern OSes
  'Segoe UI',
  'Roboto',
  'Oxygen',
  'Ubuntu',
  'Cantarell',
  'Helvetica Neue',
  'Helvetica',
  'Arial',
  'sans-serif',
].join(', ');

const monoStack = [
  'JetBrains Mono',
  'SF Mono',
  'Menlo',
  'Monaco',
  'Consolas',
  'Liberation Mono',
  'monospace',
].join(', ');

// Refined shadow scale — softer than MUI's default (which is
// quite punchy for v9 still). Each level layers a tight
// near-shadow with a wider ambient one. The first entry must
// be 'none' (MUI relies on indices 0..24).
const buildShadows = () => {
  const out = ['none'];
  for (let i = 1; i <= 24; i += 1) {
    const y = Math.round(i * 0.6);
    const blur = Math.round(i * 1.6 + 4);
    const ambientBlur = Math.round(i * 3 + 8);
    out.push(
      `0 ${y}px ${blur}px rgba(15, 22, 35, ${(0.04 + i * 0.005).toFixed(3)}), `
        + `0 ${y * 2}px ${ambientBlur}px rgba(15, 22, 35, ${(0.02 + i * 0.003).toFixed(3)})`,
    );
  }
  return out;
};

export default createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0078FF',
      light: '#4DA0FF',
      dark: '#0058BD',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#F29C27',
      light: '#F8B96A',
      dark: '#B8731A',
      contrastText: '#FFFFFF',
    },
    error: { main: '#D93025' },
    warning: { main: '#F29C27' },
    info: { main: '#0078FF' },
    success: { main: '#1FA058' },
    // Keep legacy text colors intact — many feature stylesheets
    // were tuned against the old soft-gray default and look too
    // harsh against pure-black. Anything that wants stronger
    // contrast already overrides via inline color.
    text: {
      primary: '#8c8c8c',
      secondary: '#8c8c8c',
    },
    background: {
      default: '#F6F8FB',
      paper: '#FFFFFF',
    },
    divider: 'rgba(26, 34, 49, 0.08)',
    action: {
      hover: 'rgba(0, 120, 255, 0.06)',
      selected: 'rgba(0, 120, 255, 0.12)',
      focus: 'rgba(0, 120, 255, 0.16)',
    },
    taskItem: {
      to_do: '#ff9800',
      active: '#2196f3',
      review: '#7e57c2',
      done: '#4caf50',
      failed: '#f44336',
      excluded: '#ffb8c6',
    },
  },

  shape: {
    borderRadius: 8,
  },

  shadows: buildShadows(),

  typography: {
    fontFamily: fontStack,
    fontFamilyMono: monoStack,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightSemiBold: 600,
    fontWeightBold: 700,
    h1: { fontSize: '2.25rem', fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.02em' },
    h2: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.015em' },
    h3: { fontSize: '1.5rem',  fontWeight: 600, lineHeight: 1.3,  letterSpacing: '-0.01em' },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35 },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: '1rem',    fontWeight: 600, lineHeight: 1.5 },
    subtitle1: { fontSize: '1rem',    fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 },
    body1: { fontSize: '0.9375rem', fontWeight: 400, lineHeight: 1.55 },
    body2: { fontSize: '0.875rem',  fontWeight: 400, lineHeight: 1.55 },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      letterSpacing: '0.01em',
      textTransform: 'none',
    },
    caption: { fontSize: '0.75rem', lineHeight: 1.45 },
    overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.08em', lineHeight: 1.4 },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          fontFeatureSettings: '"cv11", "ss01", "ss03"',
        },
        // Subtle scrollbar styling
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          background: 'rgba(26, 34, 49, 0.18)',
          borderRadius: 6,
          border: '2px solid transparent',
          backgroundClip: 'content-box',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(26, 34, 49, 0.30)',
          backgroundClip: 'content-box',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: { borderRadius: 8, paddingInline: 14 },
        sizeSmall: { paddingInline: 10 },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundImage: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { padding: '6px 10px' },
        head: { fontWeight: 600 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          marginInline: 4,
          paddingBlock: 6,
          '&.Mui-selected': {
            backgroundColor: '#eee',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem',
          borderRadius: 6,
          paddingBlock: 6,
          paddingInline: 10,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.9375rem',
          letterSpacing: '0.01em',
        },
      },
    },
    // No `MuiTextField.defaultProps` here on purpose: the legacy
    // forms render `<TextField>` without an explicit `variant` and
    // expect the v4-default `standard` variant (label sits above
    // the input). Forcing `variant: 'outlined'` flips the label
    // into a notched-floating position, which collides with sibling
    // selects' static labels and produces the "labels glued to the
    // top of the input" bug observed on /vessels/:id/edit. Per-form
    // outlined opt-in stays a per-Form decision.

    // Pin labels in the SHRUNK position by default. The legacy
    // `standard` variant lets MUI auto-decide based on whether the
    // input has a value — fine for native <input>, but the
    // project's react-select-based <Select> renders its own
    // placeholder/value inside the host <div>, so MUI can't detect
    // "filled" and the label collides with react-select's
    // placeholder text. Forcing `shrink: true` keeps the label
    // sitting above the input regardless of input contents — same
    // fix RHFSelect.jsx already applies per-instance, lifted to a
    // theme default so plain <TextField>s in feature forms benefit
    // too. Native <input>s are unaffected (they always rendered
    // with the label shrunk once a value was present anyway).
    MuiInputLabel: {
      defaultProps: { shrink: true },
      styleOverrides: {
        root: {
          // Slightly tighter typography for the static-shrunk
          // position — the default 1rem is heavy for a label
          // sitting above a 32-40px input.
          fontSize: '0.8125rem',
          fontWeight: 500,
          letterSpacing: '0.01em',
        },
      },
    },
    MuiOutlinedInput: {
      // Round the corners of the small subset of forms that DO
      // explicitly opt into the outlined variant. Doesn't affect
      // standard/filled variants.
      styleOverrides: {
        root: {
          borderRadius: 8,
          // Soften the resting border colour so outlined inputs
          // stop competing visually with the form's content.
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(26, 34, 49, 0.16)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(26, 34, 49, 0.28)',
          },
        },
        // Size-small variant pins to a 36px row — closer to the
        // V2 form's 32px native rows while leaving room for the
        // notched outline's overflow.
        inputSizeSmall: { paddingBlock: 8 },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        // Standard-variant inputs render an underline; align its
        // resting colour with the outlined-variant softening above
        // so the two look related rather than two different forms.
        root: {
          '&.MuiInput-underline::before': {
            borderBottomColor: 'rgba(26, 34, 49, 0.18)',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          marginTop: 4,
          marginInline: 0,
        },
      },
    },
    MuiFormControl: {
      defaultProps: { margin: 'dense' },
    },
    MuiCheckbox: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          padding: 6,
        },
      },
    },
    MuiRadio: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: { padding: 6 },
      },
    },
    MuiSwitch: {
      defaultProps: { size: 'small' },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 12 },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.0625rem',
          fontWeight: 600,
          paddingBlock: 14,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        // Tighter top spacing for compact dialogs (vessel edit,
        // user new, etc.). The MUI default of 20px above the
        // content + a 16px title bottom-padding produces 36px of
        // dead space at the top of every form.
        root: { paddingTop: '8px !important' },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '8px 16px 12px' },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, height: 6 },
      },
    },
  },

  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});
